import { cn } from '@/shared/lib/utils';
import IntlTelInput from '@intl-tel-input/react/with-utils';
import 'intl-tel-input/styles';
import { useId } from 'react';

export interface PhoneInputProps {
  value: string;
  onChange: (phone: string) => void;
  placeholder?: string;
  defaultCountry?: string;
  error?: boolean;
  id?: string;
  required?: boolean;
  disabled?: boolean;
}

export function PhoneInput({
  value,
  onChange,
  placeholder = 'Enter phone number',
  defaultCountry = 'in',
  error = false,
  id: externalId,
  required = false,
  disabled = false,
}: PhoneInputProps): JSX.Element {
  const generatedId = useId();
  const id = externalId ?? generatedId;

  const borderColor = error ? 'var(--color-coral-500)' : 'var(--border-subtle)';
  const focusColor = error ? 'var(--color-coral-500)' : 'var(--color-brand-400)';

  return (
    <div
      className={cn('intl-phone w-full min-w-0 overflow-hidden')}
      style={{ '--phone-border': borderColor, '--phone-focus': focusColor } as React.CSSProperties}
    >
      <style>{`
        .intl-phone .iti { width: 100%; }

        .intl-phone .iti__tel-input {
          height: 2.5rem;
          padding-top: 0.5rem;
          padding-bottom: 0.5rem;
          padding-right: 0.75rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          line-height: 1.25rem;
          background-color: var(--bg-surface);
          color: var(--text-primary);
          border: 1px solid var(--phone-border);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
          transition: border-color 0.15s, box-shadow 0.15s;
          outline: none;
        }
        .intl-phone .iti__tel-input::placeholder { color: var(--text-secondary); }
        .intl-phone .iti__tel-input:focus {
          border-color: var(--phone-focus);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--phone-focus) 12%, transparent);
        }
        .intl-phone .iti__tel-input:disabled {
          opacity: 0.5;
          background-color: var(--bg-surface-muted);
        }

        .intl-phone .iti__country-container {
          border-right: 1px solid var(--phone-border);
          transition: border-color 0.15s;
        }

        .intl-phone .iti__selected-dial-code { margin-left: 6px; font-size: 0.8125rem; margin-top: 4px;}
        .intl-phone .iti__country-name { color: var(--text-primary); font-size: 0.8125rem; }
        .intl-phone .iti__dial-code { color: var(--text-secondary); font-size: 0.75rem; }

        .intl-phone .iti__search-input-wrapper {
          border-bottom: 1px solid var(--border-subtle);
        }
        .intl-phone .iti__search-input {
          background-color: var(--bg-surface-muted);
          color: var(--text-primary);
          font-size: 0.8125rem;
          border-radius: 0.5rem;
        }
        .intl-phone .iti__search-input::placeholder { color: var(--text-secondary); }
        .intl-phone .iti__search-input:focus { outline: none; }

        .intl-phone .iti__search-icon-svg { stroke: var(--text-secondary); }
        .intl-phone .iti__search-clear .iti__search-clear-bg { fill: var(--text-secondary); }
        .intl-phone .iti__search-clear:hover,
        .intl-phone .iti__search-clear:focus-visible {
          background: var(--bg-surface-muted);
        }
      `}</style>
      <IntlTelInput
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        initialCountry={defaultCountry as any}
        value={value}
        onChangeNumber={onChange}
        separateDialCode
        usePreciseValidation
        inputProps={{
          id,
          placeholder,
          required,
          autoComplete: 'tel',
        }}
        disabled={disabled}
      />
    </div>
  );
}
